// ── Pre-match cinematic: the armies charge, the swords clash,
//    the kingdom is named and the battle is called. ──

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getFieldTheme } from './BattleField';

const KINGDOM_TITLE = {
  Italy:   { title: "Regno d'Italia",   motto: 'Gloria e Conquista',  crest: '⚜' },
  Spain:   { title: 'Corona di Spagna', motto: 'Plus Ultra',          crest: '🦁' },
  France:  { title: 'Reame di Francia', motto: 'Liberté et Gloire',   crest: '⚜' },
  England: { title: "Corona d'Inghilterra", motto: 'Dieu et Mon Droit', crest: '🦁' },
  Unknown: { title: 'Regno Ignoto',     motto: 'Vince chi resiste',   crest: '♛' },
};

const SwordSVG = ({ flip }) => (
  <svg width="210" height="46" viewBox="0 0 210 46" style={{ transform: flip ? 'scaleX(-1)' : 'none' }}>
    <defs>
      <linearGradient id={`blade${flip ? 'r' : 'l'}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#f8fafc" />
        <stop offset="45%" stopColor="#cbd5e1" />
        <stop offset="55%" stopColor="#94a3b8" />
        <stop offset="100%" stopColor="#475569" />
      </linearGradient>
    </defs>
    {/* blade */}
    <path d="M2 23 L138 14 L150 23 L138 32 Z" fill={`url(#blade${flip ? 'r' : 'l'})`} />
    {/* crossguard */}
    <rect x="148" y="6" width="9" height="34" rx="3" fill="#b9892e" />
    {/* grip */}
    <rect x="157" y="19" width="36" height="8" rx="4" fill="#3b2a12" />
    {/* pommel */}
    <circle cx="198" cy="23" r="9" fill="#d4af37" stroke="#8a6a18" strokeWidth="2" />
  </svg>
);

const BattleIntro = ({ kingdom = 'Unknown', players = [], onDone }) => {
  const [phase, setPhase] = useState(0);
  const bladeScale = typeof window !== 'undefined' && window.innerWidth > 760 ? 2.1 : 1.15;
  const theme = getFieldTheme(kingdom);
  const k = KINGDOM_TITLE[kingdom] || KINGDOM_TITLE.Unknown;

  useEffect(() => {
    const t = [
      setTimeout(() => setPhase(1), 1250),  // clash
      setTimeout(() => setPhase(2), 1950),  // kingdom title
      setTimeout(() => setPhase(3), 4100),  // war cry
      setTimeout(() => onDone?.(), 5500),
    ];
    return () => t.forEach(clearTimeout);
  }, [onDone]);

  return (
    <motion.div
      data-testid="battle-intro"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      onClick={() => onDone?.()}
      style={{
        position: 'fixed', inset: 0, zIndex: 300, overflow: 'hidden', cursor: 'pointer',
        background: `radial-gradient(ellipse at 50% 58%, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.82) 62%, rgba(0,0,0,0.95) 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {/* Charging dust */}
      {[0, 1].map(side => (
        <motion.div key={side}
          initial={{ x: side ? '60%' : '-60%', opacity: 0 }}
          animate={{ x: side ? '12%' : '-12%', opacity: [0, 0.6, 0.25] }}
          transition={{ duration: 1.4, ease: 'easeOut' }}
          style={{
            position: 'absolute', top: '25%', bottom: '25%', [side ? 'right' : 'left']: 0, width: '55%',
            background: `radial-gradient(ellipse at ${side ? '80%' : '20%'} 50%, rgba(150,130,110,0.4) 0%, transparent 70%)`,
            filter: 'blur(30px)',
          }}
        />
      ))}

      {/* The two blades */}
      <AnimatePresence>
        {phase < 2 && (
          <>
            <motion.div key="swl"
              initial={{ x: '-120vw', rotate: -18, opacity: 0, scale: bladeScale }}
              animate={phase === 0 ? { x: '-15vw', rotate: -8, opacity: 1, scale: bladeScale } : { x: '-7vw', rotate: -2, scale: bladeScale }}
              exit={{ opacity: 0, scale: bladeScale * 1.3 }}
              transition={{ duration: phase === 0 ? 1.25 : 0.2, ease: phase === 0 ? [0.2, 0.8, 0.2, 1] : 'easeOut' }}
              style={{ position: 'absolute', filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.9))' }}
            >
              <SwordSVG />
            </motion.div>
            <motion.div key="swr"
              initial={{ x: '120vw', rotate: 18, opacity: 0, scale: bladeScale }}
              animate={phase === 0 ? { x: '15vw', rotate: 8, opacity: 1, scale: bladeScale } : { x: '7vw', rotate: 2, scale: bladeScale }}
              exit={{ opacity: 0, scale: bladeScale * 1.3 }}
              transition={{ duration: phase === 0 ? 1.25 : 0.2, ease: phase === 0 ? [0.2, 0.8, 0.2, 1] : 'easeOut' }}
              style={{ position: 'absolute', filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.9))' }}
            >
              <SwordSVG flip />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Clash flash + sparks */}
      {phase >= 1 && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0.2, 0] }}
            transition={{ duration: 0.75, times: [0, 0.08, 0.3, 1] }}
            style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 50%, #fff 0%, rgba(255,220,150,0.6) 18%, transparent 55%)' }}
          />
          {Array.from({ length: 26 }, (_, i) => {
            const a = (i / 26) * Math.PI * 2;
            const r = 120 + (i % 5) * 60;
            return (
              <motion.div key={i}
                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                animate={{ x: Math.cos(a) * r, y: Math.sin(a) * r * 0.7, opacity: 0, scale: 0.2 }}
                transition={{ duration: 0.8 + (i % 4) * 0.15, ease: 'easeOut' }}
                style={{ position: 'absolute', width: 4, height: 4, borderRadius: '50%', background: i % 3 ? '#ffd76b' : '#fff', boxShadow: '0 0 8px #ffb638' }}
              />
            );
          })}
        </>
      )}

      {/* Kingdom title card */}
      <AnimatePresence>
        {phase === 2 && (
          <motion.div
            initial={{ opacity: 0, scale: 1.25, filter: 'blur(8px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            style={{ position: 'relative', textAlign: 'center', padding: '0 20px' }}
          >
            <motion.div
              animate={{ scale: [1, 1.08, 1], filter: [`drop-shadow(0 0 18px ${theme.torch})`, `drop-shadow(0 0 40px ${theme.torch})`, `drop-shadow(0 0 18px ${theme.torch})`] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{ fontSize: 'clamp(46px,9vw,84px)', marginBottom: 4, lineHeight: 1 }}
            >
              {k.crest}
            </motion.div>
            <div style={{ fontSize: 10, letterSpacing: '0.42em', color: 'rgba(255,215,0,0.55)', fontWeight: 800, marginBottom: 10 }}>
              THE LAST KINGDOM
            </div>
            <h1 style={{
              fontFamily: "'Cinzel',serif", margin: 0,
              fontSize: 'clamp(28px,7vw,62px)', fontWeight: 900, letterSpacing: '0.04em',
              background: 'linear-gradient(to bottom, #fff 0%, #FFEC80 42%, #FFD700 68%, #a9761a 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.9))',
            }}>
              {k.title}
            </h1>
            <div style={{ fontSize: 13, fontStyle: 'italic', color: 'rgba(255,255,255,0.45)', marginTop: 8, letterSpacing: '0.1em' }}>
              « {k.motto} »
            </div>

            {/* the four contenders */}
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 22 }}
            >
              {players.slice(0, 4).map((p, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + i * 0.12 }}
                  style={{
                    padding: '5px 13px', borderRadius: 8,
                    border: `1px solid ${theme.torch}55`, background: 'rgba(0,0,0,0.55)',
                    fontSize: 11, fontWeight: 800, color: theme.torch, fontFamily: "'Cinzel',serif",
                    letterSpacing: '0.06em',
                  }}
                >
                  {p}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* War cry */}
      <AnimatePresence>
        {phase === 3 && (
          <motion.div
            initial={{ opacity: 0, scale: 1.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ type: 'spring', stiffness: 150, damping: 14 }}
            style={{ position: 'relative', textAlign: 'center' }}
          >
            <motion.div
              animate={{ scale: [1, 4], opacity: [0.7, 0] }}
              transition={{ duration: 1, ease: 'easeOut' }}
              style={{ position: 'absolute', top: '50%', left: '50%', width: 220, height: 220, marginTop: -110, marginLeft: -110, borderRadius: '50%', border: `2px solid ${theme.torch}` }}
            />
            <h1 style={{
              fontFamily: "'Cinzel',serif", margin: 0,
              fontSize: 'clamp(30px,8vw,78px)', fontWeight: 900, letterSpacing: '0.1em', whiteSpace: 'nowrap',
              background: 'linear-gradient(to bottom, #fff2cc, #ff9f1c 55%, #8b1a0a 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 30px rgba(255,140,0,0.6))',
            }}>
              ALLE ARMI
            </h1>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Skip */}
      <button
        onClick={(e) => { e.stopPropagation(); onDone?.(); }}
        style={{
          position: 'absolute', bottom: 22, right: 22, zIndex: 5,
          background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,215,0,0.3)',
          borderRadius: 9, padding: '6px 14px', color: 'rgba(255,215,0,0.7)',
          fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', cursor: 'pointer',
        }}
      >
        SALTA ›
      </button>
    </motion.div>
  );
};

export default BattleIntro;
